import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col flex-1 min-h-0 rounded-xl bg-white dark:bg-(--colors-background-black-default) shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] dark:shadow-[0px_0px_4px_rgba(255,255,255,0.1)] overflow-hidden" };
const _hoisted_2 = { class: "flex items-center justify-between gap-3 px-5 h-14 border-b border-gainsboro dark:border-(--colors-border-base)" };
const _hoisted_3 = { class: "flex items-center gap-2" };
const _hoisted_4 = { class: "text-base font-semibold text-(--colors-text-default)" };
const _hoisted_5 = { class: "flex-1 overflow-x-hidden min-h-0" };
const _hoisted_6 = { class: "border-t border-gainsboro dark:border-(--colors-border-base) px-5 py-3 bg-white dark:bg-(--colors-background-black-default)" };
import { computed, h, onMounted, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { Button, DataTable, FacetedSearchDrawer, Pager, Tooltip, useTableQuery } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import DonationCreateModal from "@/modules/finance/presentation/components/DonationCreateModal.vue";
import { useListSettings } from "@/shared/composables/useListSettings";
import { useAsyncData } from "@/shared/composables/useAsyncData";
import { formatDate as formatDateWithProfile } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { getDonations } from "@/modules/finance/infrastructure/adapters/donationService";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { DonationStatusBadge } from "@/modules/finance/public";
export default /*@__PURE__*/ _defineComponent({
    __name: 'OrganisationDonationsTab',
    props: {
        organisationId: {},
        organisationName: {}
    },
    setup(__props) {
        const props = __props;
        const { t, locale } = useI18n();
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const { formatAmount } = useFinanceFormatters();
        const profileStore = useProfileStore();
        const formatDate = (date) => formatDateWithProfile(date, locale.value, profileStore.profile?.dateFormat);
        const ROW_OPTIONS = [10, 30, 50];
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        // ── Column settings (server-persisted via useListSettings, like OrganisationContactsTab) ──
        const columnSettings = useListSettings({
            entity: "organisation-donations",
            columns: () => [
                { key: "donationDate", label: t("organisation.detail.donations.columns.date"), visible: true, pinned: false },
                { key: "amount", label: t("organisation.detail.donations.columns.amount"), visible: true, pinned: false },
                { key: "status", label: t("organisation.detail.donations.columns.status"), visible: true, pinned: false },
                { key: "campaignName", label: t("organisation.detail.donations.columns.campaign"), visible: true, pinned: false },
            ],
            defaultOrder: ["donationDate", "amount", "status", "campaignName"],
            defaultPinning: { left: [], right: [] },
        });
        const columnOrder = computed(() => columnSettings.order.value);
        const columnPinning = computed(() => ({
            left: columnSettings.pinning.value.left ?? [],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // ── Data (server pagination) ──
        const { data: result, loading, loadError, load } = useAsyncData(() => getDonations({
            organizationId: props.organisationId,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
        }));
        watch(() => [tableQuery.state.page, tableQuery.state.pageSize], load);
        watch(() => props.organisationId, () => {
            if (tableQuery.state.page === 1)
                load();
            else
                tableQuery.setPage(1);
        });
        onMounted(load);
        const totalItems = computed(() => result.value?.totalItems ?? 0);
        const totalPages = computed(() => result.value?.totalPages ?? 0);
        const donations = computed(() => [...(result.value?.result ?? [])]);
        const emptyText = computed(() => loadError.value
            ? t("organisation.detail.donations.loadError")
            : loading.value
                ? t("organisation.detail.donations.loading")
                : t("organisation.detail.donations.empty"));
        // ── Add donation modal ────────────────────────────────────────────────────────
        const showCreateModal = ref(false);
        const createModalMounted = ref(false);
        const lockedDonor = computed(() => ({
            donorType: "Organization",
            organisationId: props.organisationId,
            displayName: props.organisationName ?? "",
        }));
        function openCreateModal() {
            createModalMounted.value = true;
            showCreateModal.value = true;
        }
        function onDonationCreated() {
            load();
        }
        const columns = computed(() => [
            {
                key: "donationDate",
                header: t("organisation.detail.donations.columns.date"),
                draggable: true,
                cell: ({ row }) => h(RouterLink, {
                    to: `/donations/${row.original.id}`,
                    class: "text-blue-600 hover:underline",
                    "data-testid": `org-donation-link-${row.original.id}`,
                }, () => formatDate(row.getValue("donationDate"))),
            },
            {
                key: "amount",
                header: t("organisation.detail.donations.columns.amount"),
                draggable: true,
                cell: ({ row }) => formatAmount(row.getValue("amount"), row.original.currency ?? "EUR"),
            },
            {
                key: "status",
                header: t("organisation.detail.donations.columns.status"),
                draggable: true,
                cell: ({ row }) => h(DonationStatusBadge, { donation: row.original }),
            },
            {
                key: "campaignName",
                header: t("organisation.detail.donations.columns.campaign"),
                draggable: true,
                cell: ({ row }) => {
                    const name = row.getValue("campaignName");
                    if (!name)
                        return "–";
                    const campaignId = row.original.campaignId;
                    if (!campaignId)
                        return name;
                    return h(RouterLink, {
                        to: { name: "CampaignDetail", params: { id: campaignId } },
                        class: "text-blue-600 hover:underline",
                        "data-testid": `org-donation-campaign-link-${row.original.id}`,
                    }, () => name);
                },
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _cache[2] || (_cache[2] = _createElementVNode("span", {
                                class: "ri-hand-heart-line text-lg text-(--colors-text-secondary)",
                                "aria-hidden": "true"
                            }, null, -1 /* CACHED */)),
                            _createElementVNode("h2", _hoisted_4, _toDisplayString(_unref(t)("organisation.detail.donations.title")), 1 /* TEXT */)
                        ]),
                        _createVNode(_unref(Tooltip), {
                            text: _unref(disabledTooltip),
                            disabled: _unref(canEdit),
                            tabindex: _unref(canEdit) ? undefined : 0
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    size: "sm",
                                    "icon-left": "ri-add-line",
                                    label: _unref(t)('organisation.detail.donations.addDonation'),
                                    disabled: !_unref(canEdit),
                                    "data-testid": "org-donation-add",
                                    onClick: openCreateModal
                                }, null, 8 /* PROPS */, ["label", "disabled"])
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["text", "disabled", "tabindex"])
                    ]),
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: donations.value,
                            "column-visibility": _unref(columnSettings).visibility,
                            "column-order": columnOrder.value,
                            "column-pinning": columnPinning.value,
                            "column-sizing": _unref(columnSettings).widths.value,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": emptyText.value,
                            "settings-aria-label": _unref(t)('columnSettings.title'),
                            onSettingsClick: _unref(columnSettings).openDrawer,
                            "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "empty-text", "settings-aria-label", "onSettingsClick", "onUpdate:columnSizing"])
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(Pager), {
                            "current-page": _unref(tableQuery).state.page,
                            "total-pages": totalPages.value,
                            "total-items": totalItems.value,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                            "items-count-separator": _unref(t)('pagination.of'),
                            "previous-label": _unref(t)('pagination.previous'),
                            "next-label": _unref(t)('pagination.next'),
                            "go-to-page-label": _unref(t)('pagination.goToPage'),
                            "go-label": _unref(t)('pagination.go'),
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ])
                ]),
                _createVNode(_unref(FacetedSearchDrawer), {
                    modelValue: _unref(columnSettings).drawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(columnSettings).drawerOpen.value) = $event)),
                    columns: _unref(columnSettings).settingsItems.value,
                    "default-columns": _unref(columnSettings).defaultSettingsItems.value,
                    "column-order": _unref(columnSettings).order.value,
                    labels: _unref(columnSettings).labels.value,
                    onSave: _unref(columnSettings).handleSave,
                    onReset: _unref(columnSettings).handleReset
                }, null, 8 /* PROPS */, ["modelValue", "columns", "default-columns", "column-order", "labels", "onSave", "onReset"]),
                (createModalMounted.value)
                    ? (_openBlock(), _createBlock(DonationCreateModal, {
                        key: 0,
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showCreateModal).value = $event)),
                        "locked-donor": lockedDonor.value,
                        onCreated: onDonationCreated
                    }, null, 8 /* PROPS */, ["modelValue", "locked-donor"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
